const MAX_TENANT_ID_LENGTH = 48

const sanitizeText = (value?: string | null): string => {
  if (typeof value !== 'string') return ''
  return value.trim()
}

const slugify = (value?: string | null): string => {
  const sanitized = sanitizeText(value)
  if (!sanitized) return ''

  const ascii = sanitized.normalize('NFD').replace(/[^\p{ASCII}]/gu, '')
  const dashed = ascii
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')

  return dashed.slice(0, MAX_TENANT_ID_LENGTH)
}

const randomSuffix = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const array = new Uint32Array(1)
    crypto.getRandomValues(array)
    return array[0].toString(36).slice(0, 6)
  }
  return Math.random().toString(36).slice(2, 8)
}

const deriveTenantName = (
  organizationName?: string | null,
  email?: string | null,
  userName?: string | null
): string => {
  const organization = sanitizeText(organizationName)
  if (organization) return organization

  const normalizedEmail = sanitizeText(email)
  if (normalizedEmail) {
    const [localPart, domain] = normalizedEmail.split('@')
    if (domain) {
      const domainName = domain.split('.')[0]
      if (domainName) return domainName
    }
    if (localPart) return localPart
  }

  const normalizedUser = sanitizeText(userName)
  if (normalizedUser) return normalizedUser

  return 'Mi empresa'
}

export const normalizeTenantId = (value?: string | null): string | null => {
  const slug = slugify(value)
  return slug || null
}

export const buildTenantIdentity = (input: {
  preferredTenantId?: string | null
  organizationName?: string | null
  email?: string | null
  userName?: string | null
}): { tenantId: string; tenantName: string } => {
  const tenantName = deriveTenantName(input.organizationName, input.email, input.userName)

  const normalizedPreferred = normalizeTenantId(input.preferredTenantId)
  if (normalizedPreferred) {
    return {
      tenantId: normalizedPreferred,
      tenantName,
    }
  }

  const candidates = [
    slugify(input.organizationName),
    slugify(input.email ? input.email.split('@')[0] : ''),
    slugify(input.userName),
  ].filter(Boolean)

  const base = candidates[0] || 'tenant'
  let tenantId = `${base}-${randomSuffix()}`
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, MAX_TENANT_ID_LENGTH)

  if (!tenantId) {
    tenantId = `tenant-${randomSuffix()}`.slice(0, MAX_TENANT_ID_LENGTH)
  }

  return {
    tenantId,
    tenantName,
  }
}
